import React, { useState, useEffect, useRef } from 'react';
import { formatDistanceToNow, isToday, isTomorrow, parseISO, format, isValid, isBefore, isAfter, subMonths } from 'date-fns';
import {
  Search, Calendar, IndianRupee, Users, Link2, Handshake,
  Cake, Download, RefreshCw, X, Check, Filter, ChevronDown,
  Eye
} from 'lucide-react';
import { GET } from '../services/api';
import TenureSelect from '../components/TenureSelect';
import { useMasterChapter, MasterChapterLockNote } from '../context/MasterChapter';
import { useNavigate, Link } from 'react-router-dom';

// --- Helper: Number Formatting ---
const formatNumber = (num) => {
  if (!num) return '0';
  return Math.floor(Number(num)).toLocaleString('en-IN');
};

// --- Toast Component (Internal) ---
const Toast = ({ message, type, onClose }) => {
  if (!message) return null;
  const bgColor = type === 'success' ? 'bg-emerald-600' : 'bg-red-500';
  return (
    <div className={`fixed bottom-6 right-6 px-6 py-3 rounded-xl shadow-2xl text-white font-bold flex items-center gap-3 z-[1000] animate-in slide-in-from-bottom-5 ${bgColor}`}>
      {type === 'success' ? <Check className="w-5 h-5" /> : <X className="w-5 h-5" />}
      {message}
      <button onClick={onClose} className="ml-auto p-1 rounded-full hover:bg-white/20 transition-colors"><X className="w-4 h-4" /></button>
    </div>
  );
};

// --- Event Card Component ---
const EventCard = ({ event, formatTimeRange }) => {
  const isMeeting = event.itemType === 'meeting';
  
  // Safe parsing dates based on type
  const startStr = isMeeting ? event.start_time : event.startDate;
  const endStr = isMeeting ? event.end_time : event.endDate;
  
  const start = parseISO(startStr);
  const end = parseISO(endStr);
  const now = new Date();

  const getMeetingStatus = () => {
    if (isBefore(now, start)) {
      if (isToday(start)) return { label: 'Today', color: 'text-green-600 bg-green-50' };
      if (isTomorrow(start)) return { label: 'Tomorrow', color: 'text-blue-600 bg-blue-50' };
      return { label: formatDistanceToNow(start, { addSuffix: true }), color: 'text-blue-600 bg-blue-50' };
    } else if (isAfter(now, start) && isBefore(now, end)) {
      return { label: 'In Progress', color: 'text-amber-600 bg-amber-50 animate-pulse' };
    } else {
      return { label: 'Ended', color: 'text-gray-500 bg-gray-100' };
    }
  };

  const statusObj = getMeetingStatus();
  
  // Normalize Location Fields
  const cityName = isMeeting ? event.city_id?.name : event.location?.city;
  const chapterName = isMeeting ? event.chapter_id?.name : null;

  return (
    <div className="group flex items-start p-4 mb-3 bg-white border border-slate-100 rounded-xl shadow-sm hover:shadow-md hover:border-blue-100 transition-all duration-200">
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-white shadow-sm mr-4 flex-shrink-0 ${isMeeting ? 'bg-gradient-to-br from-blue-500 to-blue-600' : 'bg-gradient-to-br from-teal-400 to-teal-500'}`}>
        {isMeeting ? <Handshake className="w-6 h-6" /> : <Calendar className="w-6 h-6" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex justify-between items-start">
          <h4 className="font-semibold text-slate-800 truncate pr-2">{event.title}</h4>
          <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${statusObj.color}`}>
            {statusObj.label}
          </span>
        </div>
        <p className="text-sm text-slate-500 mt-1 flex items-center gap-2">
          <span className="truncate max-w-[150px]">{cityName || 'Unknown City'}</span>
          {chapterName && (
            <>
              <span className="w-1 h-1 bg-slate-300 rounded-full"></span>
              <span className="truncate max-w-[150px]">{chapterName}</span>
            </>
          )}
        </p>
        <div className="flex items-center gap-4 mt-3 text-xs text-slate-500 font-medium">
          <div className="flex items-center gap-1 bg-slate-50 px-2 py-1 rounded-md">
            <Calendar className="w-3.5 h-3.5" />
            {format(start, 'MMM dd, yyyy')}
          </div>
          <div className="flex items-center gap-1 bg-slate-50 px-2 py-1 rounded-md">
            <ClockIcon className="w-3.5 h-3.5" />
            {formatTimeRange(startStr, endStr)}
          </div>
        </div>
      </div>
    </div>
  );
};

const ClockIcon = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
);

// --- MultiSelect Dropdown ---
const MultiSelectDropdown = ({ options, selectedValues, onSelectChange, placeholder, label, disabled = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleOptionClick = (value) => {
    let newSelectedValues;
    if (selectedValues.includes(value)) {
      newSelectedValues = selectedValues.filter(item => item !== value);
    } else {
      newSelectedValues = [...selectedValues, value];
    }
    onSelectChange(newSelectedValues);
  };

  const filteredOptions = options.filter(option =>
    option.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="relative group" ref={dropdownRef}>
      <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5 ml-1">{label}</label>
      <div
        className={`w-full p-2.5 border rounded-xl min-h-[46px] flex items-center justify-between transition-all duration-200 
        ${disabled ? 'bg-slate-50 border-slate-200 cursor-not-allowed opacity-75' : 'bg-white cursor-pointer'}
        ${!disabled && isOpen ? 'border-blue-500 ring-2 ring-blue-100 shadow-sm' : !disabled ? 'border-slate-200 hover:border-blue-300' : ''}`}
        onClick={() => { if (!disabled) setIsOpen(!isOpen); }}
        aria-disabled={disabled}
      >
        <div className="flex-grow overflow-hidden">
          {selectedValues.length === 0 ? (
            <span className="text-slate-400 text-sm">{placeholder}</span>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {selectedValues.slice(0, 2).map((value) => (
                <span key={value} className="flex items-center bg-blue-50 text-blue-700 px-2 py-0.5 rounded-md text-xs font-medium border border-blue-100">
                  {value}
                  {!disabled && <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); handleOptionClick(value); }}
                    className="ml-1 text-blue-400 hover:text-blue-600 focus:outline-none"
                  >
                    <X className="w-3 h-3" />
                  </button>}
                </span>
              ))}
              {selectedValues.length > 2 && (
                <span className="bg-slate-100 text-slate-600 px-2 py-0.5 rounded-md text-xs font-medium">
                  +{selectedValues.length - 2} more
                </span>
              )}
            </div>
          )}
        </div>
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </div>

      {isOpen && !disabled && (
        <div className="absolute top-full left-0 mt-2 w-full bg-white border border-slate-100 rounded-xl shadow-xl z-30 overflow-hidden animate-in fade-in zoom-in-95 duration-100 origin-top">
          <div className="p-2 border-b border-slate-50 bg-slate-50/50">
            <input
              type="text"
              placeholder="Search..."
              className="w-full p-2 text-sm bg-white border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoFocus
            />
          </div>
          <div className="max-h-56 overflow-y-auto p-1 custom-scrollbar">
            {filteredOptions.length === 0 && (
              <div className="p-3 text-sm text-slate-400 text-center">No results found</div>
            )}
            {filteredOptions.map(option => (
              <div
                key={option._id}
                className={`flex items-center px-3 py-2.5 rounded-lg text-sm cursor-pointer transition-colors ${selectedValues.includes(option.name) ? 'bg-blue-50 text-blue-700' : 'text-slate-700 hover:bg-slate-50'}`}
                onClick={() => handleOptionClick(option.name)}
              >
                <div className={`w-4 h-4 border rounded mr-3 flex items-center justify-center flex-shrink-0 transition-colors ${selectedValues.includes(option.name) ? 'bg-blue-600 border-blue-600' : 'border-slate-300 bg-white'}`}>
                  {selectedValues.includes(option.name) && <Check className="w-3 h-3 text-white" />}
                </div>
                <span className="truncate">{option.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// --- Stat Card Component ---
const StatCard = ({ title, value, icon: Icon, colorClass, bgClass, onDownload, onView }) => (
  <div className="group bg-white rounded-2xl p-5 border border-slate-100 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 relative overflow-hidden">
    <div className={`absolute top-0 right-0 w-24 h-24 ${bgClass} rounded-bl-full opacity-20 -mr-4 -mt-4 transition-transform group-hover:scale-110`}></div>
    <div className="flex justify-between items-start mb-4 relative z-10">
      <div>
        <p className="text-sm font-medium text-slate-500 uppercase tracking-wide">{title}</p>
        <h3 className="text-[20px] font-bold text-slate-800 mt-1">{value}</h3>
      </div>
      <div className={`w-12 h-12 ${bgClass} ${colorClass} rounded-xl flex items-center justify-center shadow-sm`}>
        <Icon className="w-6 h-6" />
      </div>
    </div>
    <div className="border-t border-slate-50 pt-3 mt-1 flex justify-between items-center relative z-20">
      {onView && (
        <button
          type="button"
          onClick={onView}
          className="flex items-center gap-1.5 text-xs font-medium text-slate-400 hover:text-blue-600 transition-colors group/view"
          title="View Data"
        >
          <Eye className="w-3.5 h-3.5 group-hover/view:scale-110 transition-transform" />
          <span>View</span>
        </button>
      )}
      {onDownload && (
        <button
          type="button"
          onClick={onDownload}
          className="flex items-center gap-1.5 text-xs font-medium text-slate-400 hover:text-green-600 transition-colors group/btn"
          title="Download Report"
        >
          <span>Download</span>
          <Download className="w-3.5 h-3.5 group-hover/btn:translate-y-0.5 transition-transform" />
        </button>
      )}
    </div>
  </div>
);

// =================================================================
// MAIN COMPONENT: DASHBOARD
// =================================================================
const EmptyState = ({ icon: Icon, children, actionTo, actionLabel }) => (
  <div className="flex flex-col items-center gap-2 px-4 py-8 text-center">
    <div className="rounded-full bg-slate-50 p-2.5 text-slate-300"><Icon className="h-5 w-5" /></div>
    <p className="text-sm text-slate-500">{children}</p>
    {actionTo && (
      <Link to={actionTo} className="text-sm font-semibold text-blue-600 hover:text-blue-700">
        {actionLabel}
      </Link>
    )}
  </div>
);

const Dashboard = () => {
  const navigate = useNavigate();
  const master = useMasterChapter();
  const [toast, setToast] = useState({ message: '', type: '' });

  const showToast = (message, type = 'info') => {
    setToast({ message, type });
    setTimeout(() => setToast({ message: '', type: '' }), 3000);
  };

  // --- Calculate Default Dates ---
  const today = new Date();
  const defaultStartDate = format(today, 'yyyy-MM-dd');
  const defaultEndDate = format(today, 'yyyy-MM-dd');

  const INITIAL_FILTER_STATE = { city: [], chapter: [], dateRange: '', tenureId: '' };

  // --- State Variables ---
  const [filterData, setFilterData] = useState(() => {
    const navEntry = window.performance.getEntriesByType("navigation")[0];
    if (navEntry && navEntry.type === 'reload') {
      sessionStorage.removeItem('dashboardFilters');
      return INITIAL_FILTER_STATE;
    }
    const saved = sessionStorage.getItem('dashboardFilters');
    return saved ? JSON.parse(saved) : INITIAL_FILTER_STATE;
  });

  const [showDatePicker, setShowDatePicker] = useState(false);
  const [dateRange, setDateRange] = useState({ startDate: defaultStartDate, endDate: defaultEndDate });

  const [cities, setCities] = useState([]);
  const [chapters, setChapters] = useState([]);
  
  const [upcomingBirthdays, setUpcomingBirthdays] = useState([]);
  const [upcomingMeetings, setUpcomingMeetings] = useState([]);
  const [renewalMemberships, setRenewalMemberships] = useState([]);

  const [totalRevenue, setTotalRevenue] = useState(0);
  const [totalMembers, setTotalMembers] = useState(0);
  const [totalReferences, setTotalReferences] = useState(0);
  const [totalMeetings, setTotalMeetings] = useState(0);
  const [totalChapters, setTotalChapters] = useState(0);

  // Sub-admin's own cards are limited to their chapters; this row shows whole-ABN counts (view/download stay scoped).
  const isSubAdmin = (() => { try { return JSON.parse(localStorage.getItem('user'))?.role === 'sub-admin'; } catch { return false; } })();
  const [abnTotals, setAbnTotals] = useState({ revenue: 0, members: 0, references: 0, meetings: 0 });

  // Super-admin master chapter: forces city/chapter (by name, as this page filters) and locks them.
  // The id lists let fetchDashboardData map those names back to ids before cities/chapters load.
  const masterChapterList = master.chapterId ? [{ _id: master.chapterId, name: master.chapterName }] : [];
  const withMasterCity = (list) =>
    master.chapterId && !list.some(c => c._id === master.cityId) ? [...list, { _id: master.cityId, name: master.cityName }] : list;
  const applyMaster = (filters) =>
    master.chapterId ? { ...filters, city: [master.cityName], chapter: [master.chapterName] } : filters;

  // Save filters to session storage
  useEffect(() => {
    sessionStorage.setItem('dashboardFilters', JSON.stringify(filterData));
  }, [filterData]);

  // --- 1. INITIAL MOUNT ONLY: Fetch Cities, Saved Chapters & Dashboard Data ---
  useEffect(() => {
    let isMounted = true;

    const initDashboard = async () => {
      try {
        // Fetch Cities
        const cData = await GET(`/dashboard/cities/`);
        const loadedCities = cData || [];
        if (isMounted) setCities(loadedCities);

        const saved = sessionStorage.getItem('dashboardFilters');
        const savedFilters = saved ? JSON.parse(saved) : INITIAL_FILTER_STATE;

        if (master.chapterId) {
          // A saved filter for some other chapter must not beat the master; its tenure goes with it.
          const sameChapter = savedFilters.chapter?.length === 1 && savedFilters.chapter[0] === master.chapterName;
          const initialFilters = applyMaster({ ...savedFilters, tenureId: sameChapter ? savedFilters.tenureId : '' });
          if (isMounted) {
            setFilterData(initialFilters);
            setChapters(masterChapterList);
            await fetchDashboardData(initialFilters, withMasterCity(loadedCities), masterChapterList);
          }
          return;
        }
        const initialFilters = savedFilters;

        // Fetch Chapters if needed by saved filters
        let loadedChapters = [];
        if (initialFilters.city && initialFilters.city.length > 0) {
          const cityIds = initialFilters.city.map(name => loadedCities.find(c => c.name === name)?._id).filter(Boolean);
          if (cityIds.length > 0) {
            const chapterPromises = cityIds.map(id => GET(`/dashboard/chapters/city/${id}`).catch(() => []));
            const chArrays = await Promise.all(chapterPromises);
            loadedChapters = Array.from(new Map(chArrays.flat().map(ch => [ch._id, ch])).values());
            if (isMounted) setChapters(loadedChapters);
          }
        }

        // Fetch All Dashboard Data
        if (isMounted) {
          await fetchDashboardData(initialFilters, loadedCities, loadedChapters);
        }
      } catch (error) {
        console.error("Initialization error:", error);
      }
    };

    initDashboard();
    return () => { isMounted = false; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- runs ONCE; master changes are handled below.

  // Master chapter changed in the header (the mount effect above covers the initial value).
  const masterFirstRun = useRef(true);
  useEffect(() => {
    if (masterFirstRun.current) { masterFirstRun.current = false; return; }
    const next = master.chapterId
      ? applyMaster({ ...filterData, tenureId: '' })
      : { ...filterData, city: [], chapter: [], tenureId: '' };
    setFilterData(next);
    setChapters(masterChapterList);
    fetchDashboardData(next, withMasterCity(cities), masterChapterList);
  }, [master.chapterId]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Fetch Core Dashboard Data function ---
  const fetchDashboardData = async (currentFilters, availableCities, availableChapters) => {
    const selectedCityIds = currentFilters.city.map(cityName => availableCities.find(city => city.name === cityName)?._id).filter(Boolean);
    const selectedChapterIds = currentFilters.chapter.map(chapterName => availableChapters.find(ch => ch.name === chapterName)?._id).filter(Boolean);
    
    const [startDate, endDate] = currentFilters.dateRange ? currentFilters.dateRange.split(' to ') : [null, null];
    
    const queryParams = new URLSearchParams();
    if (startDate && endDate) { queryParams.append("startDate", startDate); queryParams.append("endDate", endDate); }
    if (selectedCityIds.length > 0) queryParams.append("cityId", selectedCityIds.join(','));
    if (selectedChapterIds.length > 0) queryParams.append("chapterId", selectedChapterIds.join(','));
    // Tenure outranks startDate/endDate server-side; 'current' needs exactly one chapter.
    if (currentFilters.tenureId) queryParams.append("tenureId", currentFilters.tenureId);
    const qs = queryParams.toString();
    // Same date / tenure window, all chapters (backend ignores city/chapter when scope=all)
    const abnQs = qs ? `${qs}&scope=all` : 'scope=all';

    const chapterQueryParams = new URLSearchParams();
    if (selectedCityIds.length > 0) chapterQueryParams.append("cityId", selectedCityIds.join(','));
    const chapterQs = chapterQueryParams.toString();

    // Upcoming widgets only take the location filter, not the date window
    const locationParams = new URLSearchParams();
    if (selectedCityIds.length > 0) locationParams.append("cityId", selectedCityIds.join(','));
    if (selectedChapterIds.length > 0) locationParams.append("chapterId", selectedChapterIds.join(','));
    const locationQs = locationParams.toString();

    try {
      // Parallel Execution of all API calls
      const [dRevenue, dMembers, dRefs, dMeetings, dChapters, dBirthdays, dUpcoming, dRenewals] = await Promise.all([
        GET(`/dashboard/business-exchanges/total-revenue?${qs}`).catch(() => null),
        GET(`/dashboard/users/total-members?${qs}`).catch(() => null),
        GET(`/dashboard/reference-passes/total-passes?${qs}`).catch(() => null),
        GET(`/dashboard/personal-meetings/total-meetings?${qs}`).catch(() => null),
        GET(`/dashboard/chapters/count?${chapterQs}`).catch(() => null),
        GET(`/dashboard/users/upcoming-birthdays?${locationQs}`).catch(() => null),
        GET(`/dashboard/meetings/upcoming-meetings?${locationQs}`).catch(() => null),
        GET(`/dashboard/users/upcoming-renewals?${locationQs}`).catch(() => null)
      ]);

      if (isSubAdmin) {
        const [aRevenue, aMembers, aRefs, aMeetings] = await Promise.all([
          GET(`/dashboard/business-exchanges/total-revenue?${abnQs}`).catch(() => null),
          GET(`/dashboard/users/total-members?${abnQs}`).catch(() => null),
          GET(`/dashboard/reference-passes/total-passes?${abnQs}`).catch(() => null),
          GET(`/dashboard/personal-meetings/total-meetings?${abnQs}`).catch(() => null)
        ]);
        setAbnTotals({
          revenue: aRevenue?.totalRevenue || 0,
          members: aMembers?.totalMembers || 0,
          references: aRefs?.totalReferencePasses || 0,
          meetings: aMeetings?.totalPersonalMeetings || 0
        });
      }

      if (dRevenue) setTotalRevenue(dRevenue.totalRevenue || 0);
      if (dMembers) setTotalMembers(dMembers.totalMembers || 0);
      if (dRefs) setTotalReferences(dRefs.totalReferencePasses || 0);
      if (dMeetings) setTotalMeetings(dMeetings.totalPersonalMeetings || 0);
      if (dChapters) setTotalChapters(dChapters.totalChapters || 0);

      // Setup Birthdays
      if (dBirthdays) {
        const today = new Date(); today.setHours(0, 0, 0, 0);
        const enriched = dBirthdays.map((user) => {
          const dob = new Date(user.dob);
          const nextBirthday = new Date(today.getFullYear(), dob.getMonth(), dob.getDate());
          if (nextBirthday < today) nextBirthday.setFullYear(today.getFullYear() + 1);
          const daysUntil = Math.floor((nextBirthday - today) / (1000 * 60 * 60 * 24));
          return { ...user, daysUntil };
        });
        setUpcomingBirthdays(enriched);
      }

      // Setup Unified Events/Meetings logic (Fix applied here)
      if (dUpcoming) {
        const payload = dUpcoming.data || dUpcoming;
        const meetingsList = Array.isArray(payload.meetings) ? payload.meetings.map(m => ({ ...m, itemType: 'meeting' })) : [];
        const eventsList = Array.isArray(payload.events) ? payload.events.map(e => ({ ...e, itemType: 'event' })) : [];

        const combined = [...meetingsList, ...eventsList];
        
        // Sorting combined list chronologically
        combined.sort((a, b) => {
            const dateA = new Date(a.itemType === 'meeting' ? a.start_time : a.startDate);
            const dateB = new Date(b.itemType === 'meeting' ? b.start_time : b.startDate);
            return dateA - dateB;
        });

        setUpcomingMeetings(combined);
      }

      if (dRenewals && dRenewals.success && Array.isArray(dRenewals.data)) setRenewalMemberships(dRenewals.data);
    } catch (err) { 
      console.error("Error fetching dashboard data:", err); 
    }
  };

  const handleRefresh = () => {
    sessionStorage.removeItem('dashboardFilters');
    const resetFilters = applyMaster(INITIAL_FILTER_STATE);
    setFilterData(resetFilters);
    setDateRange({ startDate: defaultStartDate, endDate: defaultEndDate });
    setChapters(masterChapterList);
    fetchDashboardData(resetFilters, withMasterCity(cities), masterChapterList); 
    showToast("Dashboard refreshed & filters cleared", "success");
  };

  const handleSearch = () => {
    fetchDashboardData(filterData, cities, chapters);
    showToast("Data filtered successfully", "success");
  };

  const formatTimeRange = (start, end) => {
    if (!start || !end) return "Time not available";
    const startParsed = new Date(start);
    const endParsed = new Date(end);
    if (!isValid(startParsed) || !isValid(endParsed)) return "Invalid time";
    return `${format(startParsed, 'hh:mm a')} to ${format(endParsed, 'hh:mm a')}`;
  };

  const updateDateRange = () => {
    setFilterData({ ...filterData, dateRange: `${dateRange.startDate} to ${dateRange.endDate}` });
    setShowDatePicker(false);
  };

  const getLabel = (daysUntil) => {
    if (daysUntil === 0) return 'Today!';
    if (daysUntil === 1) return 'Tomorrow!';
    if (daysUntil > 1) return `In ${daysUntil} days`;
    return '';
  };

  const handleCitySelectChange = async (newSelectedCities) => {
    setFilterData(prev => ({ ...prev, city: newSelectedCities, chapter: [], tenureId: '' }));
    
    // Fetch chapters for newly selected cities immediately
    if (newSelectedCities.length === 0) {
      setChapters([]);
    } else {
      try {
        const cityIds = newSelectedCities.map(name => cities.find(c => c.name === name)?._id).filter(Boolean);
        const chapterPromises = cityIds.map(id => GET(`/dashboard/chapters/city/${id}`).catch(() => []));
        const chArrays = await Promise.all(chapterPromises);
        setChapters(Array.from(new Map(chArrays.flat().map(ch => [ch._id, ch])).values()));
      } catch (error) {
        console.error("Error fetching chapters:", error);
      }
    }
  };

  // TenureSelect needs one unambiguous chapter; multi-select means no tenure scoping.
  const tenureChapterId =
    filterData.chapter.length === 1
      ? (chapters.find(ch => ch.name === filterData.chapter[0])?._id || '')
      : '';

  const handleChapterSelectChange = (newSelectedChapters) => {
    // A tenure belongs to one chapter, so drop it unless exactly one stays selected.
    setFilterData(prev => ({
      ...prev,
      chapter: newSelectedChapters,
      tenureId: newSelectedChapters.length === 1 ? prev.tenureId : '',
    }));
  };

  const handleViewRedirect = (type) => {
    const selectedCityIds = filterData.city
      .map(name => cities.find(c => c.name === name)?._id)
      .filter(Boolean)
      .join(',');

    const selectedChapterIds = filterData.chapter
      .map(name => chapters.find(c => c.name === name)?._id)
      .filter(Boolean)
      .join(',');

    const [startDate, endDate] = filterData.dateRange ? filterData.dateRange.split(' to ') : ['', ''];

    navigate(`/admin/view-data/${type.toLowerCase()}`, {
      state: {
        filters: {
          cityId: selectedCityIds,
          chapterId: selectedChapterIds,
          startDate: startDate,
          endDate: endDate,
          label: `${filterData.city.length ? filterData.city.join(', ') : 'All Cities'} | ${filterData.chapter.length ? filterData.chapter.join(', ') : 'All Chapters'}`
        }
      }
    });
  };

  const handleDownload = async (cardType) => {
    let endpoint = ""; let fileName = "";
    switch (cardType) {
      case 'Revenue': endpoint = `/export-business-exchanges`; fileName = "business-exchanges.csv"; break;
      case 'Members': endpoint = `/export-members-data`; fileName = "members-data.csv"; break;
      case 'References': endpoint = `/export-reference-passes`; fileName = "reference-passes.csv"; break;
      case 'Meetings': endpoint = `/export-personal-meetings`; fileName = "personal-meetings.csv"; break;
      default: return;
    }
    showToast(`Downloading ${cardType} report...`, "info");
    const selectedCityIds = filterData.city.map(cityName => cities.find(city => city.name === cityName)?._id).filter(Boolean);
    const selectedChapterIds = filterData.chapter.map(chapterName => chapters.find(ch => ch.name === chapterName)?._id).filter(Boolean);
    
    const [startDate, endDate] = filterData.dateRange ? filterData.dateRange.split(' to ') : [null, null];
    
    const queryParams = new URLSearchParams();
    if (startDate && endDate) { queryParams.append("startDate", startDate); queryParams.append("endDate", endDate); }
    if (selectedCityIds.length > 0) queryParams.append("cityId", selectedCityIds.join(','));
    if (selectedChapterIds.length > 0) queryParams.append("chapterId", selectedChapterIds.join(','));
    if (filterData.tenureId) queryParams.append("tenureId", filterData.tenureId);

    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}${endpoint}?${queryParams.toString()}`, {
        method: 'GET', headers: { "Authorization": token ? `Bearer ${token}` : "", }
      });
      if (!response.ok) throw new Error("Failed to download file");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = fileName; document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      showToast(`${cardType} downloaded successfully`, "success");
    } catch (error) { console.error("Download Error:", error); showToast("Error downloading file", "error"); }
  };

  return (
    <div>
      <div className="space-y-5">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5">
          <div className="flex items-center justify-between mb-4 pb-3 border-b border-slate-50">
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-blue-50 rounded-lg text-blue-600">
                <Filter className="w-4 h-4" />
              </div>
              <h2 className="text-sm font-bold text-slate-700 uppercase tracking-wide">
                Filter Data
              </h2>
              <MasterChapterLockNote />
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleRefresh}
                className="flex items-center gap-2 bg-white text-slate-700 border border-slate-200 px-4 py-2 rounded-xl text-sm font-medium hover:text-blue-600 hover:border-blue-200 transition shadow-sm"
              >
                <RefreshCw className="w-4 h-4" /> Refresh
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-5">
            <MultiSelectDropdown
              label="Cities"
              placeholder="Select cities..."
              options={cities}
              selectedValues={filterData.city}
              onSelectChange={handleCitySelectChange}
              disabled={Boolean(master.chapterId)}
            />
            <MultiSelectDropdown
              label="Chapters"
              placeholder="Select chapters..."
              options={chapters}
              selectedValues={filterData.chapter}
              onSelectChange={handleChapterSelectChange}
              disabled={Boolean(master.chapterId)}
            />
            <div className="relative group">
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5 ml-1">Date Range</label>
              <button
                onClick={() => setShowDatePicker(!showDatePicker)}
                className={`w-full p-2.5 bg-white border rounded-xl text-left flex items-center justify-between transition-all duration-200 min-h-[46px]
                ${showDatePicker ? 'border-blue-500 ring-2 ring-blue-100' : 'border-slate-200 hover:border-blue-300'}`}
              >
                <div className="flex items-center gap-2 text-sm text-slate-700">
                  <Calendar className="w-4 h-4 text-blue-500" />
                  <span className="truncate">
                    {filterData.dateRange ? (
                      <>
                        {filterData.dateRange.split(' to ')[0] && format(parseISO(filterData.dateRange.split(' to ')[0]), 'MMM dd, yyyy')}
                        {' - '}
                        {filterData.dateRange.split(' to ')[1] && format(parseISO(filterData.dateRange.split(' to ')[1]), 'MMM dd, yyyy')}
                      </>
                    ) : (
                      <span className="text-slate-400">All Time</span>
                    )}
                  </span>
                </div>
                <ChevronDown className="w-4 h-4 text-slate-400" />
              </button>
              {showDatePicker && (
                <div className="absolute top-full left-0 mt-2 p-4 bg-white border border-slate-100 rounded-xl shadow-xl z-20 w-80 animate-in fade-in zoom-in-95 origin-top-left">
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div>
                      <label className="block text-xs font-semibold text-slate-500 mb-1">From</label>
                      <input type="date" value={dateRange.startDate} onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })} className="w-full p-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-500 mb-1">To</label>
                      <input type="date" value={dateRange.endDate} onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })} className="w-full p-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={updateDateRange} className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium transition">Apply</button>
                    <button onClick={() => setShowDatePicker(false)} className="flex-1 px-4 py-2 border border-slate-200 text-slate-600 rounded-lg hover:bg-slate-50 text-sm font-medium transition">Cancel</button>
                  </div>
                </div>
              )}
            </div>
            {/* Tenure — server-side this outranks the date range above */}
            <TenureSelect
              chapterId={tenureChapterId}
              value={filterData.tenureId}
              onChange={(value) => setFilterData(prev => ({ ...prev, tenureId: value }))}
              labelClassName="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5 ml-1"
              className="relative group"
            />
            <div className="relative group">
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5 ml-1 invisible">Filter</label>
              <button
                onClick={handleSearch}
                className="w-full px-6 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 active:scale-95 transition-all shadow-md shadow-blue-200 flex items-center justify-center gap-2 min-h-[46px]"
              >
                <Search className="w-4 h-4" />
                <span className="font-medium">Apply Filters</span>
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title="Business Revenue"
            value={`₹ ${formatNumber(totalRevenue)}`}
            icon={IndianRupee}
            colorClass="text-emerald-600"
            bgClass="bg-emerald-50"
            onDownload={() => handleDownload('Revenue')}
            onView={() => handleViewRedirect('Revenue')}
          />
          <StatCard
            title="Total Members"
            value={totalMembers.toLocaleString()}
            icon={Users}
            colorClass="text-blue-600"
            bgClass="bg-blue-50"
            onDownload={() => handleDownload('Members')}
            onView={() => handleViewRedirect('Members')}
          />
          <StatCard
            title="References Passed"
            value={totalReferences.toLocaleString()}
            icon={Link2}
            colorClass="text-violet-600"
            bgClass="bg-violet-50"
            onDownload={() => handleDownload('References')}
            onView={() => handleViewRedirect('References')}
          />
          <StatCard
            title="Total Meetings"
            value={totalMeetings.toLocaleString()}
            icon={Handshake}
            colorClass="text-amber-600"
            bgClass="bg-amber-50"
            onDownload={() => handleDownload('Meetings')}
            onView={() => handleViewRedirect('Meetings')}
          />
        </div>

        {isSubAdmin && (
          <div>
            <h2 className="text-sm font-bold text-slate-700 uppercase tracking-wide mb-3 ml-1">Whole ABN · All Chapters</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard title="Business Revenue" value={`₹ ${formatNumber(abnTotals.revenue)}`} icon={IndianRupee} colorClass="text-emerald-600" bgClass="bg-emerald-50" />
              <StatCard title="Total Members" value={abnTotals.members.toLocaleString()} icon={Users} colorClass="text-blue-600" bgClass="bg-blue-50" />
              <StatCard title="References Passed" value={abnTotals.references.toLocaleString()} icon={Link2} colorClass="text-violet-600" bgClass="bg-violet-50" />
              <StatCard title="Total Meetings" value={abnTotals.meetings.toLocaleString()} icon={Handshake} colorClass="text-amber-600" bgClass="bg-amber-50" />
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 flex flex-col">
            <div className="p-5 border-b border-slate-50 flex items-center justify-between">
              <h3 className="font-bold text-slate-800 flex items-center gap-2">
                <div className="p-1.5 bg-pink-50 rounded-lg text-pink-500"><Cake className="w-4 h-4" /></div>
                Birthdays
              </h3>
              <span className="text-xs font-medium text-slate-400 bg-slate-50 px-2 py-1 rounded-full">Upcoming</span>
            </div>
            <div className="p-4 space-y-3 overflow-y-auto max-h-[400px] custom-scrollbar">
              {upcomingBirthdays.length === 0 ? (
                <EmptyState icon={Cake}>No birthdays coming up in this window.</EmptyState>
              ) : (
                upcomingBirthdays.map((user) => (
                  <div key={user._id} className="flex items-center p-3 hover:bg-slate-50 rounded-xl transition-colors group cursor-default">
                    <div className="w-10 h-10 bg-gradient-to-br from-pink-400 to-rose-500 rounded-full flex items-center justify-center text-white text-sm font-bold shadow-sm mr-3 group-hover:scale-105 transition-transform">
                      {`${user.first_name?.[0] || ""}${user.last_name?.[0] || ""}`.toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-slate-800 text-sm truncate">{user.first_name} {user.last_name}</p>
                      <p className="text-xs text-slate-500">{new Date(user.dob).toLocaleDateString(undefined, { month: 'long', day: 'numeric' })}</p>
                    </div>
                    <span className={`text-xs font-semibold px-2 py-1 rounded-lg ${getLabel(user.daysUntil).includes('Today') ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}`}>
                      {getLabel(user.daysUntil)}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 flex flex-col lg:col-span-1">
            <div className="p-5 border-b border-slate-50 flex items-center justify-between">
              <h3 className="font-bold text-slate-800 flex items-center gap-2">
                <div className="p-1.5 bg-blue-50 rounded-lg text-blue-600"><Calendar className="w-4 h-4" /></div>
                Events & Meetings
              </h3>
            </div>
            <div className="p-4 overflow-y-auto max-h-[400px] custom-scrollbar">
              {upcomingMeetings.length === 0 ? (
                <EmptyState icon={Calendar} actionTo="/admin/meeting" actionLabel="Schedule a meeting">Nothing on the calendar yet.</EmptyState>
              ) : (
                upcomingMeetings.map((event) => (
                  <EventCard key={event._id} event={event} formatTimeRange={formatTimeRange} />
                ))
              )}
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 flex flex-col">
            <div className="p-5 border-b border-slate-50 flex items-center justify-between">
              <h3 className="font-bold text-slate-800 flex items-center gap-2">
                <div className="p-1.5 bg-cyan-50 rounded-lg text-cyan-500"><RefreshCw className="w-4 h-4" /></div>
                Renewals
              </h3>
              <div className="flex items-center gap-2">
                <span className="bg-red-50 text-red-600 text-xs font-bold px-2 py-1 rounded-full">{renewalMemberships.length} Due</span>
                <Link to="/admin/membership" className="text-xs font-semibold text-blue-600 hover:text-blue-700">View all</Link>
              </div>
            </div>
            <div className="p-4 space-y-3 overflow-y-auto max-h-[400px] custom-scrollbar">
              {renewalMemberships.length > 0 ? (
                renewalMemberships.map((membership) => {
                  const daysUntil = membership.remainingDays;
                  return (
                    <div key={membership.membershipId} className="flex flex-col p-4 border border-slate-100 rounded-xl bg-slate-50 hover:border-blue-200 transition-colors">
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-white border border-slate-200 flex items-center justify-center text-xs font-bold text-slate-600">{membership.full_name?.[0]}</div>
                          <div>
                            <p className="font-bold text-slate-800 text-sm">{membership.full_name}</p>
                            <p className="text-xs text-slate-500">{membership.chapter || '-'}</p>
                          </div>
                        </div>
                        <span className="text-sm font-bold text-slate-800">₹{Number(membership.price || 0).toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between items-center mt-2 pt-2 border-t border-slate-200/50">
                        <span className="text-xs text-slate-500">Expires: <span className="font-medium text-slate-700">{format(parseISO(membership.endDate), 'MMM dd, yyyy')}</span></span>
                        <span className={`text-xs font-bold ${daysUntil < 0 ? 'text-red-600' : 'text-amber-600'}`}>
                          {daysUntil < 0 ? `Overdue ${-daysUntil}d` : daysUntil === 0 ? 'Ends today' : `${daysUntil} days left`}
                        </span>
                      </div>
                    </div>
                  );
                })
              ) : (
                <EmptyState icon={RefreshCw}>No memberships are due for renewal.</EmptyState>
              )}
            </div>
          </div>
        </div>
      </div>
      {toast.message && <Toast message={toast.message} type={toast.type} onClose={() => setToast({ message: '', type: '' })} />}
    </div>
  );
};

export default Dashboard;